<template>
  <InfuseDescriptionList :rows="rows" :item="item" :loading="loading">
    <template #alias="{ data }">
      {{ data.join(', ') }}
    </template>

    <template #ocppVersion="{ data }">OCPP-{{ data }} </template>

    <template #useManualURI="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #useRequestHeader="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #omitSoapHeader="{ data }">
      <font-awesome-icon
        :icon="['fas', data ? 'circle-check' : 'circle-xmark']"
        :class="data ? 'text-primary-500' : 'text-neutral-500'"
      ></font-awesome-icon>
    </template>

    <template #basicAuthPassword>●●●●●●●●●●●●●●●●●●●●</template>

    <template #basicAuthLastPasswordChange="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #updatedAt="{ data }">
      {{ formatDate(data) }}
    </template>
  </InfuseDescriptionList>
</template>

<script lang="ts" setup>
import { InfuseDescriptionList } from '@infuse/ui/components';
import { UiDescriptionListRow } from '@infuse/ui/domain';
import { computed, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { ConnectionType } from '@infuse/e-connector/central-system/domain';
import { formatDate } from '@infuse/ui/utils';

interface Props {
  item: ChargingStation;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const { t } = useI18n();

const rows: ComputedRef<UiDescriptionListRow[]> = computed(() => [
  {
    name: 'name',
    term: t('e_connector.common.chargingStation.name'),
    detailField: 'name'
  },
  {
    name: 'alias',
    term: t('e_connector.common.chargingStation.alias'),
    detailField: 'alias',
    hidden: !props.item.alias || props.item.alias.length === 0
  },
  {
    name: 'type',
    term: t('e_connector.common.chargingStation.type'),
    detailField: 'type'
  },
  {
    name: 'ocppVersion',
    term: t('e_connector.common.chargingStation.ocppVersion'),
    detailField: 'ocppVersion'
  },
  {
    name: 'uri',
    term: t('e_connector.common.chargingStation.url'),
    detailField: 'uri',
    hidden: !props.item.uri || !props.item.useManualURI || props.item.type !== ConnectionType.SOAP,
    copyable: true
  },
  {
    name: 'useRequestHeader',
    term: t('e_connector.common.chargingStation.useRequestHeader'),
    detailField: 'useRequestHeader',
    hidden: props.item.type !== ConnectionType.SOAP
  },
  {
    name: 'omitSoapHeader',
    term: t('e_connector.common.chargingStation.omitSoapHeader'),
    detailField: 'omitSoapHeader',
    hidden: props.item.type !== ConnectionType.SOAP
  },
  {
    name: 'basicAuthStatus',
    term: t('e_connector.common.chargingStation.basicAuthStatus'),
    detailField: 'basicAuthStatus'
  },
  {
    name: 'basicAuthPassword',
    term: t('e_connector.common.chargingStation.basicAuthPassword'),
    detailField: 'basicAuthPassword',
    copyable: true,
    hidden: !props.item.basicAuthPassword
  },
  {
    name: 'basicAuthLastPasswordChange',
    term: t('e_connector.common.chargingStation.basicAuthLastPasswordChange'),
    detailField: 'basicAuthLastPasswordChange',
    hidden: !props.item.basicAuthPassword
  },
  {
    name: 'networkProfileName',
    term: t('e_connector.common.chargingStation.networkProfile'),
    detailField: 'networkProfileName'
  },
  {
    name: 'updatedAt',
    term: t('e_connector.common.chargingStation.lastUpdatedAt'),
    detailField: 'updatedAt'
  }
]);
</script>
